// src/modules/cafe-menu/api.ts
// Cafe Menu module — API client and TypeScript types

export interface CafeMenuItem {
  clover_item_id: string;
  name: string;
  display_name: string | null;
  size_label: string | null;
  description: string | null;
  price: number | null;
  sku: string | null;
  category: string | null;
  hidden: boolean;
  available: boolean;
  recipe_id: number | null;
  recipe_name: string | null;
  recipe_status: string | null;
  cogs: number | null;
  cogs_calculated_at: string | null;
  margin: number | null;
  margin_pct: number | null;
  last_synced_at: string | null;
}

export interface CogsBreakdownLine {
  type: 'raw_material' | 'sub_recipe';
  name: string;
  quantity: number | null;
  unit: string;
  converted_qty?: number | null;
  base_unit?: string | null;
  unit_price: number | null;
  line_cost: number | null;
  warning: string | null;
  sub_breakdown?: CogsBreakdownLine[];
}

export interface CogsHistory {
  id: number;
  recipe_id: number | null;
  cogs: number | null;
  warnings: string[] | null;
  triggered_by: string | null;
  calculated_at: string;
}

export interface MenuSummary {
  total: number;
  linked: number;
  unlinked: number;
  with_cogs: number;
  avg_margin_pct: number | null;
}

export interface MenuListResponse {
  items: CafeMenuItem[];
  last_synced_at: string | null;
  summary: MenuSummary;
}

export interface SyncResult {
  total: number;
  inserted: number;
  updated: number;
  cogs_recalculated: number;
  synced_at: string;
}

// ============================================================================
// Helpers
// ============================================================================
const headers = (token: string | null, extra?: Record<string, string>) => ({
  'X-Session-Token': token || '',
  ...(extra || {}),
});

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try { msg = JSON.parse(text).error || text; } catch { /* raw text */ }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ============================================================================
// API calls
// ============================================================================

export interface MenuFilters {
  search?: string;
  category?: string;
  has_recipe?: boolean;
  available?: boolean;
  include_hidden?: boolean;
}

export const listMenuItems = (token: string | null, filters: MenuFilters = {}) => {
  const qs = new URLSearchParams();
  if (filters.search) qs.set('search', filters.search);
  if (filters.category) qs.set('category', filters.category);
  if (filters.has_recipe !== undefined) qs.set('has_recipe', String(filters.has_recipe));
  if (filters.available !== undefined) qs.set('available', String(filters.available));
  if (filters.include_hidden) qs.set('include_hidden', 'true');
  const q = qs.toString() ? `?${qs}` : '';
  return fetch(`/api/cafe-menu${q}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<MenuListResponse>);
};

export const getMenuItem = (token: string | null, id: string) =>
  fetch(`/api/cafe-menu/${id}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ item: CafeMenuItem; cogs_breakdown: CogsBreakdownLine[] | null; cogs_warnings: string[] | null }>);

export const getCogsHistory = (token: string | null, id: string) =>
  fetch(`/api/cafe-menu/${id}/cogs-history`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ history: CogsHistory[] }>);

export const syncMenu = (token: string | null) =>
  fetch('/api/cafe-menu/sync', { method: 'POST', headers: headers(token) })
    .then(handle<SyncResult>);

export const updateMenuItem = (token: string | null, id: string, body: {
  description?: string | null;
  size_label?: string | null;
  display_name?: string | null;
  recipe_id?: number | null;
}) =>
  fetch(`/api/cafe-menu/${id}`, {
    method: 'PATCH',
    headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ item: CafeMenuItem }>);

export const recalculateCogs = (token: string | null, id: string) =>
  fetch(`/api/cafe-menu/${id}/recalculate`, { method: 'POST', headers: headers(token) })
    .then(handle<{ cogs: number | null; breakdown: CogsBreakdownLine[]; warnings: string[] }>);
